/**
 * pages/Transformasi.js
 * Drive the transformation engine. Pick type + bucket, build editable input/output
 * lines (LineEditor can be pre-filled from a named recipe), preview the before/after
 * cascade, then review and confirm.
 *
 * Pressing "Jalankan Transformasi" validates (≥1 input AND ≥1 output — else a red error
 * shown in the Run card directly above the button, nothing commits) then opens a read-only
 * "Tinjau Transformasi" receipt (TransformReceiptModal). The stock cascade and the post-run
 * save-recipe prompt happen ONLY after "Konfirmasi Transformasi". The engine's missing-item
 * error also renders in the Run card. Both errors are independent and stack.
 *
 * Bottom of the page: three separate cards —
 *   1. Catatan (note field)
 *   2. Simpan sebagai resep — disabled + named info banner when current combo already matches
 *      a saved recipe (live findMatchingRecipe check, independent of templateRef)
 *   3. Run card — validation + engine errors directly above "Jalankan Transformasi"
 *
 * The success message (okMsg) stays at the top. Named recipes pre-fill the form via "Isi dari
 * resep". After a confirmed run, if the combo is new, SaveRecipeModal opens automatically.
 */
import React, { useEffect, useMemo, useState } from "react";
import { fmtQty, today } from "../utils/idGenerators";
import { bucketLabel } from "../utils/transformations";
import { findMatchingRecipe } from "../utils/recipeMatch";
import { siblingsOf } from "../utils/siblings";
import ItemPickerModal from "../components/ItemPickerModal";
import SaveRecipeModal from "../components/SaveRecipeModal";
import TransformReceiptModal from "../components/TransformReceiptModal";
import SetNameModal from "../components/SetNameModal";
import LineEditor from "../components/LineEditor";
import Combobox from "../components/Combobox";

const TYPES = [
  { key: "ASSEMBLE", label: "Assemble (Rakit)" },
  { key: "TEARDOWN", label: "Teardown (Urai)" },
];

function collectPendingCategories(pItems) {
  const seen = new Set();
  const result = [];
  for (const p of pItems) {
    if (p.newCategory && !seen.has(p.newCategory.code)) {
      seen.add(p.newCategory.code);
      result.push(p.newCategory);
    }
  }
  return result;
}

export default function Transformasi({ items, recipes = [], initialTargetCode, onSubmit, onSaveRecipe, onPreviewItemCreate, onCommitPendingItems, categories = [], siblingLinks = [], onDone, onViewHistory }) {
  const [type, setType] = useState("ASSEMBLE");
  const [bucket, setBucket] = useState("PUSAT");
  const [inputs, setInputs] = useState([]);
  const [outputs, setOutputs] = useState([]);
  const [picker, setPicker] = useState(null); // { side:"input"|"output", lineIndex:number|null } | null
  const [notes, setNotes] = useState("");
  const [templateRef, setTemplateRef] = useState(null); // recipe id used to prefill (if any)
  const [validationError, setValidationError] = useState(""); // missing input/output
  const [engineError, setEngineError] = useState("");         // engine: missing item
  const [okMsg, setOkMsg] = useState("");

  const [pendingTxn, setPendingTxn] = useState(null);   // validated txn awaiting confirm | null
  const [recipeModal, setRecipeModal] = useState(null);  // save-recipe snapshot | null
  const [createModal, setCreateModal] = useState(null);  // null | { mode: "set" | "choose" }
  const [pendingItems, setPendingItems] = useState([]);  // [{ internalCode, name, isSet, category, newCategory }]

  const pendingCodes = useMemo(() => new Set(pendingItems.map((p) => p.internalCode)), [pendingItems]);

  const byCode = useMemo(() => {
    const m = new Map(items.map((it) => [it.internalCode, it]));
    for (const p of pendingItems) {
      if (!m.has(p.internalCode)) m.set(p.internalCode, { ...p, stokPusat: 0, stokCabang: 0 });
    }
    return m;
  }, [items, pendingItems]);

  // Smart-default ordering for the pickers — a guide, NOT a wall. Every item stays selectable in
  // both lists; only the display order changes so the likely choice sits on top. Stable sort.
  const isSetItem = (it) => it.isSet === true;
  const sortRanked = (rank) => [...items].sort((a, b) => rank(a) - rank(b));
  const inputItems = useMemo(
    () =>
      type === "TEARDOWN"
        ? items.filter(isSetItem)                      // Urai source must be a SET (assembly)
        : sortRanked((it) => (isSetItem(it) ? 1 : 0)), // Rakit: all items, spare parts first
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, type]
  );
  const outputItems = useMemo(
    // Rakit: SETs first (the thing being built). Urai: resulting parts (spare parts) first.
    () => sortRanked(type === "TEARDOWN" ? (it) => (isSetItem(it) ? 1 : 0) : (it) => (isSetItem(it) ? 0 : 1)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, type]
  );

  // Named recipes for the current direction (both types supported); offered in "Isi dari resep".
  const recipesForType = useMemo(
    () => recipes.filter((r) => r.type === type).sort((a, b) => (a.name || "").localeCompare(b.name || "")),
    [recipes, type]
  );

  const recipeOptions = useMemo(
    () => recipesForType.map((r) => ({
      value: r.id,
      primary: r.name,
      search: (r.name || "").toLowerCase(),
    })),
    [recipesForType]
  );

  /** Prefill the form from a named recipe: restore type + both sides (symmetric). */
  const prefillFromRecipe = (recipeId) => {
    setValidationError(""); setEngineError(""); setOkMsg("");
    const r = recipes.find((x) => x.id === recipeId);
    if (!r) { setTemplateRef(null); return; }
    setType(r.type);
    setInputs((r.inputs || []).map((l) => ({ internalCode: l.internalCode, qty: l.qty })));
    setOutputs((r.outputs || []).map((l) => ({ internalCode: l.internalCode, qty: l.qty })));
    setTemplateRef(r.id);
  };

  // If opened pre-targeted from a Detail page, prefill once on mount.
  useEffect(() => {
    if (!initialTargetCode) return;
    // Prefer the first assemble recipe that PRODUCES this code; otherwise seed it as the single output.
    const match = recipes.find((r) => r.type === "ASSEMBLE" &&
      (r.outputs || []).some((o) => o.internalCode === initialTargetCode));
    if (match) {
      prefillFromRecipe(match.id);
    } else if (byCode.has(initialTargetCode)) {
      setType("ASSEMBLE");
      setOutputs([{ internalCode: initialTargetCode, qty: 1 }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onTypeChange = (t) => {
    setType(t);
    // Switching type abandons the current build entirely — start the new type from a clean form.
    setInputs([]);
    setOutputs([]);
    setNotes("");
    setTemplateRef(null);
    setPendingItems([]);
    setValidationError(""); setEngineError(""); setOkMsg("");
  };

  // ── Before/after preview: net delta per item in the chosen bucket ───────────
  // This is reused as-is by TransformReceiptModal (passed as the `preview` prop).
  const preview = useMemo(() => {
    const net = new Map();
    const add = (code, d) => { if (code) net.set(code, (net.get(code) || 0) + d); };
    for (const l of inputs) add(l.internalCode, -(Number(l.qty) || 0));
    for (const l of outputs) add(l.internalCode, Number(l.qty) || 0);
    const field = bucket === "CABANG" ? "stokCabang" : "stokPusat";
    return [...net.entries()].map(([code, delta]) => {
      const it = byCode.get(code);
      const before = it ? Number(it[field]) || 0 : 0;
      return { code, name: it?.name || code, delta, before, after: before + delta, short: before + delta < 0 };
    });
  }, [inputs, outputs, bucket, byCode]);

  // ── Sibling-substitution hints for short input lines ─────────────────────────
  // A line is "short" using the same trigger as the preview's ⚠ (net after < 0 in this bucket).
  // Suggestions per input line: that code's siblings that exist, have positive stock in the
  // current bucket, and aren't already on another input line — sorted by that stock, descending.
  const previewByCode = useMemo(() => new Map(preview.map((p) => [p.code, p])), [preview]);
  const stockField = bucket === "CABANG" ? "stokCabang" : "stokPusat";
  const inputSuggestions = useMemo(() => {
    const currentCodes = new Set(inputs.map((l) => l.internalCode).filter(Boolean));
    return inputs.map((line) => {
      const code = line.internalCode;
      if (!code) return [];
      const p = previewByCode.get(code);
      if (!p || !p.short) return [];
      return siblingsOf({ siblingLinks }, code)
        .map((sc) => byCode.get(sc))
        .filter((it) => it && !currentCodes.has(it.internalCode) && (Number(it[stockField]) || 0) > 0)
        .sort((a, b) => (Number(b[stockField]) || 0) - (Number(a[stockField]) || 0))
        .map((it) => ({ code: it.internalCode, stock: Number(it[stockField]) || 0 }));
    });
  }, [inputs, previewByCode, siblingLinks, byCode, stockField]);

  /** Swap an input line's code to a suggested sibling; tag the new line so downstream
   *  (receipt, engine, riwayat) can show/record it was a stock-shortage substitution. */
  const onSubstituteInput = (lineIndex, siblingCode) => {
    setInputs((lines) => lines.map((l, i) => (i === lineIndex
      ? { internalCode: siblingCode, qty: l.qty, substituteFor: l.internalCode }
      : l
    )));
  };

  // Validate and open the review receipt. Engine runs only after the user presses
  // "Konfirmasi Transformasi" in the receipt modal.
  const submit = (e) => {
    e.preventDefault();
    setValidationError(""); setEngineError(""); setOkMsg("");
    const cleanIn = inputs.filter((l) => l.internalCode && Number(l.qty) > 0);
    const cleanOut = outputs.filter((l) => l.internalCode && Number(l.qty) > 0);
    // Issue 1 guard: a transformation must have at least one input AND one output.
    if (cleanIn.length === 0 || cleanOut.length === 0) {
      setValidationError("Transformasi harus punya minimal satu input dan satu output.");
      return; // receipt does not open; nothing commits
    }
    setPendingTxn({
      type, bucket,
      inputs: cleanIn,
      outputs: cleanOut,
      date: new Date().toISOString(),
      notes,
      templateRef,
    });
  };

  // Commit the engine run after the user confirms in the receipt. Mirrors what the old submit tail did.
  const confirmTransform = () => {
    const txn = pendingTxn;
    if (!txn) return;
    // Commit any pending inline-created items that appear in the outputs BEFORE the engine runs.
    // update() is synchronous on dataRef, so the engine sees the new items immediately.
    const outCodes = new Set(txn.outputs.map((l) => l.internalCode));
    const toCommit = pendingItems.filter((p) => outCodes.has(p.internalCode));
    const skipped = toCommit.length ? onCommitPendingItems(toCommit) : [];
    setValidationError(""); setEngineError("");
    const res = onSubmit(txn, toCommit.map((p) => p.internalCode));
    if (!res.ok) {
      setPendingTxn(null);
      // A skipped pending item means the engine's rejection below is about to fire on a raw,
      // unhelpful code ("Item tidak ditemukan: ZZ-00001") — that item was never minted at all,
      // so name it and why instead of the engine's generic missing-item message. The engine's
      // own rejection stays the backstop either way; this only makes the shown message honest.
      setEngineError(skipped.length
        ? `Gagal membuat barang baru: ${skipped.map((s) => `"${s.name}" (${s.error})`).join(", ")}. Transformasi tidak disimpan.`
        : res.error);
      return;
    }
    setPendingTxn(null);
    setValidationError(""); setEngineError("");
    setOkMsg("Transformasi berhasil disimpan dan stok telah dicascade.");
    const snapshot = { type: txn.type, inputs: txn.inputs, outputs: txn.outputs };
    setInputs([]); setOutputs([]); setNotes(""); setTemplateRef(null);
    setPendingItems([]);
    if (txn.inputs.length > 0 && txn.outputs.length > 0 && !findMatchingRecipe(recipes, snapshot)) {
      setRecipeModal({
        ...snapshot,
        intro: "Transformasi berhasil dijalankan. Kombinasi ini belum ada di daftar resep — simpan supaya bisa dipakai lagi nanti?",
      });
    }
  };

  // ── "Simpan sebagai resep" — create a named recipe (either type) from the current sides ─
  const cleanInputs = inputs.filter((l) => l.internalCode && Number(l.qty) > 0);
  const cleanOutputs = outputs.filter((l) => l.internalCode && Number(l.qty) > 0);
  // A recipe needs at least one input and one output.
  const canSaveRecipe = cleanInputs.length > 0 && cleanOutputs.length > 0;
  // Live match: does the CURRENT form combo already exist as a saved recipe?
  const liveMatch = (cleanInputs.length > 0 && cleanOutputs.length > 0)
    ? findMatchingRecipe(recipes, { type, inputs: cleanInputs, outputs: cleanOutputs })
    : null;
  // Can save only if there's a full combo AND it isn't already a saved recipe.
  const canSaveRecipeNow = canSaveRecipe && !liveMatch;
  // A pending item's code isn't in the catalog until confirm — block recipe save until then.
  const hasUncommittedPending = outputs.some((l) => pendingItems.some((p) => p.internalCode === l.internalCode));

  // Open the save modal on the current sides (manual entry point — no intro).
  const openSaveRecipe = () => setRecipeModal({ type, inputs: cleanInputs, outputs: cleanOutputs });

  // Build the recipe from the open snapshot. Saving a recipe never touches stock — it only adds a recipe.
  const handleSaveRecipe = ({ name, note }) => {
    const snap = recipeModal;
    if (!snap) return;
    onSaveRecipe({
      type: snap.type, name, note,
      inputs: snap.inputs.map((l) => ({ internalCode: l.internalCode, qty: Number(l.qty) || 0 })),
      outputs: snap.outputs.map((l) => ({ internalCode: l.internalCode, qty: Number(l.qty) || 0 })),
    });
    setRecipeModal(null);
    setOkMsg(`Resep "${name}" disimpan.`);
  };

  /** Drop the picked codes into the active side. Add mode (lineIndex null) appends deduped lines;
   *  replace mode swaps the one line's item. */
  const applyPick = (codes) => {
    if (!picker) return;
    const { side, lineIndex } = picker;
    const setLines = side === "input" ? setInputs : setOutputs;
    setLines((lines) => {
      if (lineIndex == null) {
        const existing = new Set(lines.map((l) => l.internalCode).filter(Boolean));
        const additions = codes.filter((c) => !existing.has(c)).map((c) => ({ internalCode: c, qty: 1 }));
        return [...lines.filter((l) => l.internalCode), ...additions];
      }
      return lines.map((l, i) => {
        if (i !== lineIndex) return l;
        // A manual re-pick is no longer a substitution — drop substituteFor if it was set.
        const { substituteFor, ...rest } = l;
        return { ...rest, internalCode: codes[0] };
      });
    });
    setPicker(null);
  };

  /** Hold a new item as PENDING in the form; committed atomically at "Konfirmasi Transformasi".
   *  Code is previewed against saved + currently pending items so sequential codes are correct.
   *  newCategoryCode carries the user's explicit code when a brand-new category was typed. */
  const createOutputItem = ({ name, isSet, category, newCategoryCode }) => {
    const clean = (name || "").trim();
    if (!clean) return;
    const { internalCode, newCategory } = onPreviewItemCreate(
      category, pendingItems, collectPendingCategories(pendingItems), newCategoryCode
    );
    const pending = { internalCode, name: clean, isSet: !!isSet, category: (category || "").trim(), newCategory };
    setPendingItems((prev) => [...prev, pending]);
    setOutputs((lines) => {
      if (lines.some((l) => l.internalCode === internalCode)) return lines;
      return [...lines.filter((l) => l.internalCode), { internalCode, qty: 1 }];
    });
  };

  return (
    <>
    <form onSubmit={submit}>
      <div className="page-header">
        <div>
          <h1>Transformasi</h1>
          <div className="page-sub">Rakit / Urai — stok dicascade secara atomik.</div>
        </div>
        <div style={{ display: "flex", gap: "var(--gap-sm)" }}>
          {onViewHistory && <button type="button" className="btn" onClick={onViewHistory}>Riwayat Transformasi</button>}
          {onDone && <button type="button" className="btn" onClick={onDone}>Tutup</button>}
        </div>
      </div>

      {okMsg && <div className="alert alert--ok">{okMsg}</div>}

      <div className="card">
        <div className="form-grid">
          <div className="form-row">
            <label className="form-label">Jenis</label>
            <div className="seg">
              {TYPES.map((t) => (
                <button type="button" key={t.key} className={type === t.key ? "active" : ""}
                  onClick={() => onTypeChange(t.key)}>{t.label}</button>
              ))}
            </div>
          </div>
          <div className="form-row">
            <label className="form-label">Bucket</label>
            <div className="seg">
              <button type="button" className={bucket === "PUSAT" ? "active" : ""} onClick={() => setBucket("PUSAT")}>Pusat</button>
              <button type="button" className={bucket === "CABANG" ? "active" : ""} onClick={() => setBucket("CABANG")}>Cabang</button>
            </div>
          </div>
        </div>

        {recipesForType.length > 0 && (
          <div className="form-row">
            <label className="form-label" htmlFor="transformasi-isi-resep">Isi dari resep (opsional)</label>
            <Combobox
              id="transformasi-isi-resep"
              value={templateRef || null}
              options={recipeOptions}
              onChange={(id) => (id ? prefillFromRecipe(id) : setTemplateRef(null))}
              allowCreate={false}
              placeholder="Cari atau pilih resep…"
            />
            <div className="form-hint">Resep hanya mengisi form. Anda bebas menambah/ubah/hapus baris sebelum simpan.</div>
          </div>
        )}
      </div>

      <LineEditor title={`Input (dikonsumsi dari ${bucketLabel(bucket)})`}
        lines={inputs} byCode={byCode} onChange={setInputs}
        max={type === "TEARDOWN" ? 1 : undefined}
        maxHint={type === "TEARDOWN" ? "Urai memakai tepat satu barang sumber." : undefined}
        onAdd={() => setPicker({ side: "input", lineIndex: null })}
        onReplace={(i) => setPicker({ side: "input", lineIndex: i })}
        suggestions={inputSuggestions}
        onSubstitute={onSubstituteInput} />
      <LineEditor title={`Output (diproduksi ke ${bucketLabel(bucket)})`}
        lines={outputs} byCode={byCode} onChange={setOutputs}
        onCreateSet={type === "ASSEMBLE" ? () => setCreateModal({ mode: "set" }) : undefined}
        onCreateItem={type === "TEARDOWN" ? () => setCreateModal({ mode: "choose" }) : undefined}
        onAdd={() => setPicker({ side: "output", lineIndex: null })}
        onReplace={(i) => setPicker({ side: "output", lineIndex: i })}
        pendingCodes={pendingCodes} />

      {preview.length > 0 && (
        <div className="card">
          <div className="card__title">Pratinjau perubahan stok ({bucketLabel(bucket)})</div>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr><th>Barang</th><th className="num">Sebelum</th><th className="num">Perubahan</th><th className="num">Sesudah</th></tr>
              </thead>
              <tbody>
                {preview.map((p) => (
                  <tr key={p.code} style={{ cursor: "default" }}>
                    <td><span className="mono">{p.code}</span> · {p.name}{pendingCodes.has(p.code) && <span className="chip chip--baru" style={{ marginLeft: "var(--gap-sm)" }}>baru</span>}</td>
                    <td className="num">{fmtQty(p.before)}</td>
                    <td className="num" style={{ color: p.delta >= 0 ? "var(--green)" : "var(--red)" }}>
                      {p.delta > 0 ? `+${p.delta}` : p.delta}
                    </td>
                    <td className="num total-cell" style={{ color: p.short ? "var(--red)" : undefined }}>
                      {fmtQty(p.after)}{p.short ? " ⚠" : ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {preview.some((p) => p.short) && (
            <div className="form-hint">
              ⚠ Sebagian stok akan menjadi negatif. Ini diperbolehkan (mis. stok pinjaman) dan
              transformasi tetap akan berjalan.
            </div>
          )}
        </div>
      )}

      {/* BOX 1 — Catatan */}
      <div className="card">
        <div className="form-row">
          <label className="form-label" htmlFor="transformasi-catatan">Catatan</label>
          <input id="transformasi-catatan" className="form-input" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={`Tanggal ${today()}`} />
        </div>
      </div>

      {/* BOX 2 — Save as recipe */}
      <div className="card">
        <div className="card__title">Simpan sebagai resep</div>
        {liveMatch && (
          <div className="alert alert--info" style={{ marginBottom: "var(--gap-md)" }}>
            Kombinasi input/output ini sudah tersimpan sebagai resep <b>"{liveMatch.name}"</b>.
          </div>
        )}
        <div className="transform-saverecipe">
          <button type="button" className="btn btn-soft" disabled={!canSaveRecipeNow || hasUncommittedPending}
            title={
              !canSaveRecipe ? "Perlu minimal satu input dan satu output."
              : hasUncommittedPending ? "Selesaikan transformasi dulu untuk menyimpan item baru sebagai resep."
              : liveMatch ? `Kombinasi ini sudah ada sebagai resep "${liveMatch.name}".`
              : ""
            }
            onClick={openSaveRecipe}>
            <span className="btn-circle-plus" aria-hidden="true">+</span>
            Simpan sebagai resep
          </button>
          <span className="form-hint">Simpan kombinasi ini sebagai template untuk dipakai lagi — tidak mengubah stok.</span>
        </div>
      </div>

      {/* BOX 3 — Run */}
      <div className="card">
        {validationError && <div className="alert alert--error" style={{ marginBottom: "var(--gap-md)" }}>{validationError}</div>}
        {engineError && <div className="alert alert--error" style={{ marginBottom: "var(--gap-md)" }}>{engineError}</div>}
        <div className="transform-runrow">
          <button type="submit" className="btn btn-primary btn-lg">Jalankan Transformasi</button>
        </div>
      </div>
    </form>

    {picker && (
      <ItemPickerModal
        items={picker.side === "input" ? inputItems : outputItems}
        bucket={bucket}
        multi={picker.lineIndex == null && !(picker.side === "input" && type === "TEARDOWN")}
        hideTypeFilter={picker.side === "input" && type === "TEARDOWN"}
        onConfirm={applyPick}
        onClose={() => setPicker(null)}
      />
    )}

    {pendingTxn && (
      <TransformReceiptModal
        type={pendingTxn.type}
        bucket={pendingTxn.bucket}
        inputLines={pendingTxn.inputs}
        outputLines={pendingTxn.outputs}
        preview={preview}
        byCode={byCode}
        pendingCodes={pendingCodes}
        onConfirm={confirmTransform}
        onClose={() => setPendingTxn(null)}
      />
    )}

    {recipeModal && (
      <SaveRecipeModal
        snapshot={recipeModal}
        intro={recipeModal.intro}
        byCode={byCode}
        recipes={recipes}
        onSave={handleSaveRecipe}
        onClose={() => setRecipeModal(null)}
      />
    )}

    {createModal && (
      <SetNameModal
        mode={createModal.mode}
        categories={[...categories, ...collectPendingCategories(pendingItems)]}
        onCreate={(payload) => { createOutputItem(payload); setCreateModal(null); }}
        onClose={() => setCreateModal(null)}
      />
    )}
    </>
  );
}
